import { BookOpen, Check, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetPublicArticleByIdQuery, useVoteArticleMutation } from '../../store/apis/helpCenter.api';
import Modal2 from '../common/Modal2';
import { HelpArticleSkeleton } from './Skeleton';
import { ArticleBlocks } from './ArticleBlocks';
import { getReadTime, mapModuleToCategory } from '../../utils/helpCenter';
import { formatDDMMYYYY } from '../../utils/timeUtils';
import { scrollToTop } from '../survey/hooks';
import toast from 'react-hot-toast';

interface HelpArticleProps {
    articleId?: string;
    onBack?: () => void;
}

/** Figma article reader modal: category pill + meta + blocks + feedback (dc.html:4036). */
export function HelpArticle({ articleId, onBack }: HelpArticleProps) {
    const { id } = useParams();

    const activeId = id || articleId;
    const { data: article, isLoading, isError } = useGetPublicArticleByIdQuery(activeId!);
    const [voteArticle, { isLoading: isVoting }] = useVoteArticleMutation();
    const [votedLocally, setVotedLocally] = useState<boolean>(false);

    // Reset feedback state and scroll to top when article changes
    useEffect(() => {
        setVotedLocally(false);
        scrollToTop();
    }, [activeId]);

    const handleClose = () => {
        if (onBack) onBack();
    };

    const hasVoted = votedLocally || !!article?.hasVoted;

    const handleVote = async (helpful: boolean) => {
        if (!article || hasVoted || isVoting) return;
        try {
            await voteArticle({ id: article._id, helpful }).unwrap();
            setVotedLocally(true);
            toast.success('Thank you for your feedback!');
        } catch (error: any) {
            if (error?.status === 409) {
                setVotedLocally(true);
                toast.error('You have already submitted feedback for this article.');
            } else {
                toast.error('Failed to submit feedback. Please try again.');
            }
        }
    };

    const renderBlocks = (content: string) => <ArticleBlocks content={content} />;


    const catStyle = article ? mapModuleToCategory(article.moduleCode) : null;
    const catLabel = article ? mapModuleToCategory(article.moduleCode).label : "";

    return (
        <Modal2
            isOpen
            onClose={handleClose}
            width={680}
            headerClassName="sm:pt-6"
            bodyPadding={false}
            bodyClassName="p-[22px_24px]"
            title={
                article ? (
                    <div className="pr-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-caption font-bold px-2.5 py-1 rounded-full mb-2.5 ${catStyle?.tileBg} ${catStyle?.tileColor}`}>
                            <BookOpen className="w-[13px] h-[13px]" />
                            {catLabel}
                        </span>
                        <div className="text-heading-md font-extrabold text-[var(--color-text-primary)] leading-tight">
                            {article.title}
                        </div>
                        <p className="mt-[7px] text-body-xs text-[var(--color-text-tertiary)]">
                            Updated {formatDDMMYYYY(article.updatedAt) ?? 'Recently'} · {getReadTime(article.content)}
                        </p>
                    </div>
                ) : undefined
            }
        >
            {isLoading ? (
                <HelpArticleSkeleton />
            ) : isError || !article ? (
                <div className="text-center py-10">
                    <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load article</h3>
                    <p className="text-body-sm text-[var(--color-text-secondary)]">Please close and try again.</p>
                </div>
            ) : (
                <div>
                    {article.imageUrl && (
                        <figure className="my-1.5 mb-4">
                            <div className="w-full min-h-[200px] rounded-md border border-[var(--color-border-primary)] bg-[var(--color-bg-hover)] flex items-center justify-center overflow-hidden">
                                <img
                                    src={article.imageUrl}
                                    alt={article.title}
                                    className="max-w-full max-h-[400px] w-auto h-auto object-contain"
                                />
                            </div>
                        </figure>
                    )}

                    <div className="mb-[22px]">{renderBlocks(article.content)}</div>

                    {/* Figma feedback box */}
                    <div className="p-4 rounded-md bg-[var(--color-bg-hover)] border border-[var(--color-border-primary)]">
                        {!hasVoted ? (
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <span className="text-label font-bold text-[var(--color-text-primary)]">
                                    Was this helpful?
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={isVoting}
                                        onClick={() => handleVote(true)}
                                        className="h-11 px-3.5 flex items-center gap-1.5 border border-[var(--color-layout-dropdown-badge-border)] bg-[var(--color-bg-base)] rounded-button text-body-sm font-bold text-[var(--color-success-dark)] cursor-pointer hover:border-[var(--color-success)] transition-colors focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none disabled:opacity-50"
                                    >
                                        <ThumbsUp className="w-[15px] h-[15px]" />
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isVoting}
                                        onClick={() => handleVote(false)}
                                        className="h-11 px-3.5 flex items-center gap-1.5 border border-[var(--color-layout-dropdown-badge-border)] bg-[var(--color-bg-base)] rounded-button text-body-sm font-bold text-[var(--color-text-secondary)] cursor-pointer hover:border-[var(--color-error-light)] transition-colors focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none disabled:opacity-50"
                                    >
                                        <ThumbsDown className="w-[15px] h-[15px]" />
                                        No
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-[11px]">
                                <span className="w-[34px] h-[34px] rounded-button flex-none grid place-items-center bg-[var(--color-success-light)]">
                                    <Check className="w-[18px] h-[18px] text-[var(--color-success-dark)]" />
                                </span>
                                <div>
                                    <div className="text-label font-bold text-[var(--color-text-primary)]">
                                        Thanks for your feedback!
                                    </div>
                                    <div className="text-body-xs text-[var(--color-text-tertiary)]">
                                        Your response has been recorded.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Modal2>
    );
}

export default HelpArticle;
